import { isNil, omitBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiCalendar } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import ConfirmModal from '../../app/modals/ConfirmModal';
import { IPropsNotificationListItem } from '../../app/notifications/components/NotificationListItem';
import FilterAndSearchPresenter from '../../app/notifications/presenters/FilterAndSearchPresenter';
import NotificationListTablePresenter from '../../app/notifications/presenters/NotificationListTablePresenter';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import { PintoAnnouncementNotificationType } from '../../core/graphql/types';
import useMutationPintoAnnouncementNotificationDelete from '../../hooks/useMutationPintoAnnouncementNotificationDelete';
import useQueryPintoAnnouncementNotifications from '../../hooks/useQueryPintoAnnouncementNotifications';

const StatusOptions = [
  { label: 'All', value: null },
  { label: 'Notified', value: true },
  { label: 'Not Notified', value: false },
];

const NotificationPage = () => {
  const { pintoAnnouncementNotifications, fetchPintoAnnouncementNotifications, isLoadingPintoAnnouncementNotifications } = useQueryPintoAnnouncementNotifications();
  const { deleteNoti } = useMutationPintoAnnouncementNotificationDelete();

  const [searchText, setSearchText] = useState<string | undefined>(undefined);

  const [status, setStatus] = useState<boolean | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  //modal
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [notiId, setNoti] = useState<string | undefined>(undefined);

  const handleChangePage = useCallback(
    (page: number) => {
      const offset = (page - 1) * 10;
      setCurrentPage(page);
      const filter = omitBy({ status, offset }, isNil);
      fetchPintoAnnouncementNotifications(filter);
    },
    [fetchPintoAnnouncementNotifications, status],
  );

  useEffect(() => {
    const filter = omitBy({ notified: status, q: searchText }, isNil);
    fetchPintoAnnouncementNotifications(filter);
  }, [fetchPintoAnnouncementNotifications, searchText, status]);

  const handleSetSearchText = useCallback((text) => {
    setSearchText(text);
  }, []);

  const handleOnDelete = useCallback((id: string) => {
    setShowConfirmDelete(true);
    setNoti(id);
  }, []);

  const onSubmitDelete = useCallback(() => {
    if (notiId) {
      deleteNoti(notiId);
      setShowConfirmDelete(false);
      ToastComponent({
        label: 'Terminate successfully',
        type: IToastType.SUCCESS,
      });
    }
  }, [deleteNoti, notiId]);

  return (
    <div className="mx-auto space-y-24">
      {showConfirmDelete && <ConfirmModal onClose={() => setShowConfirmDelete(false)} onConFirm={() => onSubmitDelete()} title="Terminate" subTitle="Are you confirm to delete?" />}

      <div className="space-y-24 divider-y">
        {/* section: title */}
        <div className="flex justify-between py-5">
          <h1 className="font-dbh text-[28px]">Notifications</h1>

          <div className="space-x-16">
            <Link to="/notifications/create?calendar=true">
              <button className="space-x-8 btn btn-ghost">
                <FiCalendar className="font-bold text-24" />
                <span>Notification Calendar</span>
              </button>
            </Link>
            <Link to="/notifications/create">
              <button className="space-x-8 btn btn-error">
                <FiPlus className="font-bold text-24" />
                <span>Create New Notification</span>
              </button>
            </Link>
          </div>
        </div>
        {/* filter and search */}
        <div className="space-y-8">
          <div className="text-colorsBrandWarmBlack02LabelSecondary">Sort by: Created Date</div>
          <FilterAndSearchPresenter onSetSearchText={handleSetSearchText} status={status} onSetStatus={(_status) => setStatus(_status)} statusOptions={StatusOptions} />
        </div>
      </div>

      {/* table content */}
      <div>
        {isLoadingPintoAnnouncementNotifications && <div className="w-full btn btn-outline btn-disabled bg-systemGrays04LabelQuaternary border-none loading h-[100px]" />}

        {!isLoadingPintoAnnouncementNotifications && (
          <>
            {(pintoAnnouncementNotifications?.edges?.length || 0) === 0 && (
              <div className="flex items-center justify-center w-full h-[60vh] text-colorsBrandWarmBlack02LabelSecondary">No list of {status} notification</div>
            )}

            {(pintoAnnouncementNotifications?.edges?.length || 0) !== 0 && (
              <NotificationListTablePresenter
                notificationLists={pintoAnnouncementNotifications?.edges.map(
                  ({ node }) =>
                    ({
                      _id: node._id,
                      title: node.title,
                      url: node.clickActionData,
                      description: node.description,
                      notifiedAt: node?.notifiedAt,
                      platforms: node.platforms,
                      createdAt: node.createdAt,
                      notified: node.notified,
                      type: node.type as unknown as PintoAnnouncementNotificationType,
                    } as IPropsNotificationListItem),
                )}
                currentPage={currentPage}
                totalCount={pintoAnnouncementNotifications?.totalCount}
                onChangePage={handleChangePage}
                onDelete={handleOnDelete}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationPage;
